import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { handleNPWorkflowStatusChange } from '@app/utils/WFforInvoice';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { updateInvoice } from '@app/api/labs.api';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import NPInvoice from '@app/components/apps/NominalPayments/NPInvoice';
import { MakeInvoicePayment } from '@app/api/finances.api';

const NomPayInvoicePage: React.FC = () => {
  const location = useLocation();
  const { id, invoiceId, status, labStatus, wfActions, lab_code, total, model } = location.state || {};
  const [currentBreadcrumName, setCurrentBreadcrumName] = useState<string>('Invoice');
  const { setBreadcrumb, clearHeaderButtons, addHeaderButton } = useHeader();
  const navigate = useNavigate();
  const handleErrorMessages = useApiErrorHandler();
  const isInvoiceModel = model === 'INV';

  const routes = [
    {
      path: '/nominal-payments',
      breadcrumbName: 'Nominal Payments',
    },
    {
      path: '/view-nominal-payment',
      breadcrumbName: lab_code,
      state: { id: id },
    },
    {
      path: '/view-invoice',
      breadcrumbName: currentBreadcrumName,
    },
  ].filter((route) => route);

  useEffect(() => {
    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);
    addHeaderButton(
      'Download PDF',
      <BaseButton type="primary" size="small" onClick={handleDownloadPDF}>
        Download Invoice
      </BaseButton>,
    );
    if (labStatus === 'PENDING_PAYMENT') {
      addHeaderButton(
        'Make Payment',
        <BaseButton type="primary" size="small" onClick={handleMakePayment}>
          Make Payment
        </BaseButton>,
      );
    }

    addHeaderButton(
      'Close',
      <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <BaseButton
          type="default"
          size="small"
          danger
          onClick={() =>
            navigate('/view-nominal-payment', {
              state: { id, activeTab: '7' },
            })
          }
        >
          Close
        </BaseButton>
      </div>,
    );

    return () => {
      setBreadcrumb(undefined);
      clearHeaderButtons();
    };
  }, [setBreadcrumb, clearHeaderButtons, addHeaderButton, currentBreadcrumName, routes, status]);

  const handleDownloadPDF = () => {
    const invoiceElement = document.getElementById('invoice-content');

    if (!invoiceElement) {
      console.error('Invoice content not found!');
      return;
    }

    html2canvas(invoiceElement, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const marginTop = 15;
      const marginBottom = 10;

      const usablePageHeight = pageHeight - marginTop - marginBottom;

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const scaleRatio = pageWidth / canvasWidth;
      const scaledCanvasHeight = canvasHeight * scaleRatio;
      const sliceHeightInPx = usablePageHeight / scaleRatio;

      let position = 0;
      let pageIndex = 0;

      while (position < canvasHeight) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.min(sliceHeightInPx, canvasHeight - position);

        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, position, canvasWidth, pageCanvas.height, 0, 0, canvasWidth, pageCanvas.height);

          const imgData = pageCanvas.toDataURL('image/png');
          if (pageIndex > 0) pdf.addPage();

          const imgHeightInMm = pageCanvas.height * scaleRatio;

          pdf.addImage(imgData, 'PNG', 0, marginTop, pageWidth, imgHeightInMm);
        }

        position += sliceHeightInPx;
        pageIndex++;
      }

      pdf.save(`invoice-${id}.pdf`);
    });
  };

  // const handleMakePayment = () => {
  //   const userType = localStorage.getItem('User_Type');

  //   const modalInstance = Modal.info({
  //     title: 'Select Payment Method',
  //     content: (
  //       <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
  //         {userType !== 'CUSTOMER' && (
  //           <BaseButton
  //             onClick={() => {
  //               modalInstance.destroy();
  //               Modal.confirm({
  //                 title: 'Confirm Cash Payment',
  //                 content: 'Has the cash been received?',
  //                 okText: 'Yes',
  //                 cancelText: 'No',
  //                 onOk: async () => {
  //                   try {
  //                     await updateInvoice(invoiceId);
  //                     message.success('Cash payment confirmed.');
  //                     handlePostPaymentStatusChange();
  //                   } catch (err) {
  //                     handleErrorMessages(err);
  //                     console.error('Invoice update error:', err);
  //                   }
  //                 },
  //               });
  //             }}
  //           >
  //             Cash Payment
  //           </BaseButton>
  //         )}
  //         <BaseButton
  //           onClick={async () => {
  //             modalInstance.destroy();
  //             try {
  //               await updateInvoice(invoiceId);
  //               message.success('Card payment confirmed.');
  //               handlePostPaymentStatusChange();
  //             } catch (err) {
  //               handleErrorMessages(err);
  //               console.error('Invoice update error:', err);
  //             }
  //           }}
  //         >
  //           Card Payment
  //         </BaseButton>
  //       </div>
  //     ),
  //     okButtonProps: { style: { display: 'none' } },
  //   });
  // };

  const handleMakePayment = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = (storedUser?.usertype ?? '').toUpperCase();

    const modalInstance = Modal.info({
      title: 'Select Payment Method',
      content: renderPaymentButtons(),
      okButtonProps: { style: { display: 'none' } },
      maskClosable: true,
      focusTriggerAfterClose: false,
    });

    function renderPaymentButtons() {
      return (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          {userType !== 'CUSTOMER' && (
            <BaseButton
              onClick={() => {
                modalInstance.update({
                  content: renderCashInput(),
                  title: `Pay LKR ${total}?`,
                });
              }}
            >
              Cash Payment
            </BaseButton>
          )}
          <BaseButton
            onClick={async () => {
              modalInstance.destroy();
              try {
                await updateInvoice(invoiceId);
                message.success('Card payment confirmed.');
                handlePostPaymentStatusChange();
              } catch (err) {
                handleErrorMessages(err);
                console.error('Invoice update error:', err);
              }
            }}
          >
            Card Payment
          </BaseButton>
        </div>
      );
    }

    function renderCashInput() {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
          {/* <p> Pay LKR {total}?</p> */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <BaseButton
              size="small"
              onClick={() => {
                // Cancel and go back to payment buttons
                modalInstance.update({
                  content: renderPaymentButtons(),
                  title: 'Select Payment Method',
                });
              }}
            >
              Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              size="small"
              onClick={() => {
                if (!total) {
                  message.error('Total amount not available.');
                  return;
                }

                // Show confirmation modal for entered amount
                Modal.confirm({
                  title: 'Confirm Cash Payment',
                  content: `Pay LKR ${total}?`,
                  okText: 'Confirm & Pay',
                  cancelText: 'Cancel',
                  okButtonProps: { size: 'small' },
                  cancelButtonProps: { size: 'small' },
                  onOk: async () => {
                    modalInstance.destroy();
                    try {
                      if (isInvoiceModel) {
                        await MakeInvoicePayment(invoiceId, 'CASH', total);
                        message.success(`Cash payment of ${total} confirmed.`);
                      } else {
                        await updateInvoice(invoiceId);
                        message.success(`Cash payment of ${total} confirmed.`);
                      }
                      handlePostPaymentStatusChange();
                    } catch (err) {
                      handleErrorMessages(err);
                      console.error('Cash payment error:', err);
                    }
                  },
                  onCancel: () => {
                    // Do nothing; user can re-enter amount
                  },
                });
              }}
            >
              Pay
            </BaseButton>
          </div>
        </div>
      );
    }
  };

  const handlePostPaymentStatusChange = async () => {
    if (id && labStatus && wfActions.length > 0) {
      await handleNPWorkflowStatusChange(id, labStatus, wfActions, (newStatus) => {
        console.log('Task status updated to:', newStatus);
        navigate(`/view-nominal-payment`, { state: { id: id } });
      });
    }
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <NPInvoice id={invoiceId} setCurrentBreadcrumName={setCurrentBreadcrumName} />
        </Col>
      </Row>
    </>
  );
};

export default NomPayInvoicePage;
